import {
  DeleteOutlined,
  EditOutlined,
  FormOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import {
  AButton,
  ACheckBox,
  AInput,
  AModal,
  ASelect,
  ASwitch,
  ATitle,
} from '../../../components';
import { IInput } from '../../../shared/interfaces';
import { useToast } from '../../../hooks/toast';
import { useCallback, useEffect, useState } from 'react';
import { Col, Form, Row } from 'antd';
import { schemaInput } from '../schemas/yup.input.schema';
import { IInputOption } from '../../../shared/interfaces';
import { FormOptionsContainer } from './styles';
import api from '../../../services/api';
import { INPUT_TYPES } from '../../../shared/enums/input_types.enums';
import { useI18n } from '../../../shared/i18n';

interface IInputCreateEditProps {
  open: boolean;
  close: () => void;
  input: IInput | null | undefined;
  inputs: IInput[];
  setInputs: React.Dispatch<React.SetStateAction<IInput[]>>;
  id: string | undefined;
}

const InputCreateEdit: React.FC<IInputCreateEditProps> = ({
  open,
  close,
  input,
  inputs,
  setInputs,
  id,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const [type, setType] = useState<string>();
  const [label, setLabel] = useState<string>();
  const [required, setRequired] = useState<boolean>(false);
  const [status, setStatus] = useState<boolean>(false);
  const [order, setOrder] = useState<number>();
  const [options, setOptions] = useState<IInputOption[]>([]);

  const cleanFields = () => {
    setType('');
    setLabel('');
    setOptions([]);
    setRequired(false);
  };

  const handleCreate = useCallback(async () => {
    schemaInput
      .validate({
        type: type ?? '',
        label: label ?? '',
        required: required,
      })
      .then(async (validated) => {
        if (
          ['select', 'checkbox'].includes(validated.type) &&
          !options.length
        ) {
          addToast({
            type: 'error',
            title: t(
              'modelsFormsCreateEdit.inputModal.validation.noOptionsTitle',
            ),
            description: t(
              'modelsFormsCreateEdit.inputModal.validation.noOptionsDescription',
            ),
          });
          return;
        }

        if (
          ['select', 'checkbox'].includes(validated.type) &&
          optionIsEmpty()
        ) {
          addToast({
            type: 'error',
            title: t(
              'modelsFormsCreateEdit.inputModal.validation.emptyOptionsTitle',
            ),
            description: t(
              'modelsFormsCreateEdit.inputModal.validation.emptyOptionsDescription',
            ),
          });
          return;
        }
        let inputInsert: IInput = { ...validated, id: uuidv4(), options };

        if (id !== 'new') {
          inputInsert.form_id = id;
          inputInsert.order = inputs.length + 1;
          await api
            .post('/inputs', inputInsert)
            .then((response) => {
              inputInsert = response.data;
              addToast({
                type: 'success',
                title: t('modelsFormsCreateEdit.inputModal.toast.successTitle'),
                description: t(
                  'modelsFormsCreateEdit.inputModal.toast.created',
                ),
              });
              setInputs((state) => [...state, inputInsert]);
              cleanFields();
              close();
            })
            .catch((error) => {
              addToast({
                type: 'error',
                title: t('modelsFormsCreateEdit.inputModal.toast.errorTitle'),
                description:
                  error.response.data.message ??
                  t('modelsFormsCreateEdit.inputModal.toast.createError'),
              });
            });
        } else {
          addToast({
            type: 'success',
            title: t('modelsFormsCreateEdit.inputModal.toast.successTitle'),
            description: t('modelsFormsCreateEdit.inputModal.toast.created'),
          });
          setInputs((state) => [...state, inputInsert]);
          cleanFields();
          close();
        }
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('modelsFormsCreateEdit.inputModal.toast.blankFields'),
          description: err.errors[0],
        });
      });
  }, [input, type, label, required, status, options, t]);

  const handleAddOption = useCallback(() => {
    const option: IInputOption = {
      label: '',
      value: '',
    };
    setOptions((state) => [...state, option]);
  }, [options]);

  const optionIsEmpty = () => {
    let isEmpty = false;
    options.forEach((opt) => {
      if (!opt.value) {
        isEmpty = true;
      }
    });
    return isEmpty;
  };

  const handlerDataEachOption = useCallback(
    (index: number, value: string) => {
      options[index] = options[index].input_id
        ? { ...options[index], value, label: value }
        : { value, label: value };
      setOptions([...options]);
    },
    [options],
  );

  const handleDeleteFieldOption = useCallback(
    (index: number) => {
      options.splice(index, 1);
      setOptions([...options]);
    },
    [options],
  );

  const handleUpdateFieldOption = useCallback(
    (index: number, status?: boolean) => {
      options[index] = { ...options[index], status: !status };
      setOptions([...options]);
    },
    [options],
  );

  const handleEdit = useCallback(async () => {
    if (!input) return;

    let inputEdited: IInput = {
      label: label ?? '',
      type: type ?? '',
      required: required,
      order: order,
      options: options,
    };

    if (input?.form_id) {
      inputEdited.status = status;
      await api
        .patch(`/inputs/${input.id}`, inputEdited)
        .then((response) => {
          inputEdited = response.data;
          setInputs((state: IInput[]) =>
            state.map((e) => {
              if (e.id !== input?.id) {
                return e;
              }
              e = inputEdited;
              return e;
            }),
          );
          addToast({
            type: 'info',
            title: t('modelsFormsCreateEdit.inputModal.toast.infoTitle'),
            description: t('modelsFormsCreateEdit.inputModal.toast.updated'),
          });
          close();
        })
        .catch((error) => {
          const message = error.response.data.message;
          addToast({
            type: 'error',
            title: t('modelsFormsCreateEdit.inputModal.toast.errorTitle'),
            description:
              message ?? t('modelsFormsCreateEdit.inputModal.toast.editError'),
          });
        });
    }
  }, [input, type, label, required, status, order, options, t]);

  useEffect(() => {
    if (input) {
      setOptions(input.options);
      setType(input.type);
      setLabel(input.label);
      setOrder(input.order);
      setRequired(input.required ?? false);
      setStatus(input.status ?? false);
    }
  }, [input]);

  const renderOptions = () => {
    if (type && ['select', 'checkbox'].includes(type)) {
      return (
        <FormOptionsContainer>
          <ATitle level={4}>
            {t('modelsFormsCreateEdit.inputModal.fields.options')}
          </ATitle>
          <Row justify="end">
            <AButton
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={handleAddOption}
            >
              {t('modelsFormsCreateEdit.inputModal.fields.addOption')}
            </AButton>
          </Row>
          {options.map((opt, index) => (
            <Row style={{ display: 'flex', alignItems: 'center' }} key={opt.id}>
              <Col span={22}>
                <AInput
                  placeholder={t(
                    'modelsFormsCreateEdit.inputModal.fields.optionValuePlaceholder',
                  )}
                  value={opt.value}
                  onChange={(e) => handlerDataEachOption(index, e.target.value)}
                />
              </Col>
              <Col span={1}>
                {!input?.form_id ? (
                  <AButton
                    danger
                    type="primary"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteFieldOption(index)}
                  />
                ) : (
                  <ASwitch
                    style={{ margin: '10px 0px' }}
                    size="small"
                    checked={opt.status}
                    onClick={() => handleUpdateFieldOption(index, opt.status)}
                  />
                )}
              </Col>
            </Row>
          ))}
        </FormOptionsContainer>
      );
    }
  };

  return (
    <AModal
      open={open}
      close={close}
      title={
        input ? (
          <>
            <EditOutlined /> {t('modelsFormsCreateEdit.inputModal.titleEdit')}
          </>
        ) : (
          <>
            <FormOutlined /> {t('modelsFormsCreateEdit.inputModal.titleCreate')}
          </>
        )
      }
      onOk={input ? handleEdit : handleCreate}
    >
      <Form layout="vertical" style={{ maxWidth: 600 }}>
        <Form.Item
          label={t('modelsFormsCreateEdit.inputModal.fields.type')}
          required
        >
          <ASelect
            placeholder={t(
              'modelsFormsCreateEdit.inputModal.fields.typePlaceholder',
            )}
            value={type}
            onChange={(e) => setType(e)}
            options={INPUT_TYPES}
          />
        </Form.Item>
        <Form.Item
          label={t('modelsFormsCreateEdit.inputModal.fields.question')}
          required
        >
          <AInput
            placeholder={t(
              'modelsFormsCreateEdit.inputModal.fields.questionPlaceholder',
            )}
            name="label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </Form.Item>
        {input?.form_id && (
          <Form.Item
            label={t('modelsFormsCreateEdit.inputModal.fields.order')}
            required
          >
            <AInput
              placeholder={t(
                'modelsFormsCreateEdit.inputModal.fields.orderPlaceholder',
              )}
              name="order"
              type="number"
              value={order}
              onChange={(e) => setOrder(+e.target.value)}
            />
          </Form.Item>
        )}
        <ACheckBox onChange={() => setRequired(!required)} checked={required}>
          {t('modelsFormsCreateEdit.inputModal.fields.required')}
        </ACheckBox>
        {input?.form_id && (
          <Form.Item
            label={t('modelsFormsCreateEdit.inputModal.fields.active')}
          >
            <ASwitch
              checked={status}
              checkedChildren={t('common.yes')}
              unCheckedChildren={t('common.no')}
              onChange={() => {
                setStatus(!status);
              }}
            />
          </Form.Item>
        )}
      </Form>
      {renderOptions()}
    </AModal>
  );
};

export default InputCreateEdit;
